/**
 * GRN on-time performance chart for a PO line.
 * Shows percentage breakdown: On Time / Early / Late across all GRNs for the same PO line.
 * CR-4614: Click-through drill-down showing full GRN list.
 * CR-4648: Drill-down table now exposes per-row Appeal Status + Action so
 * users can acknowledge / appeal / cancel-appeal / respond directly from
 * the sibling-deliveries view, mirroring the buttons already offered on
 * the parent Delivery Details modal but scoped to the clicked GRN.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../lib/api';
import type { GoodsReceiptNote, DeliveryAppeal, DeliveryAppealStatus } from '../../../lib/api/types/grn-types';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { AcknowledgeGRNModal, type GRNWorkflowData } from '../GRNWorkflowModals';
import RaiseAppealModal from './RaiseAppealModal';
import AppealResponseModal from './AppealResponseModal';

interface GRNScoreChartProps {
  poLineId: number;
  poNumber?: string;
  lineNumber?: number;
}

const COLORS: Record<string, string> = {
  on_time: '#22c55e',
  early: '#3b82f6',
  late: '#ef4444',
  unknown: '#9ca3af',
};

const LABELS: Record<string, string> = {
  on_time: 'On Time',
  early: 'Early',
  late: 'Late',
  unknown: 'Unknown',
};

/** CR-4648: Mirror DeliveryTab.getAppealStatusBadge so the drill-down's
 * Appeal Status column matches the parent list's labelling exactly.
 */
function appealStatusCell(grn: GoodsReceiptNote): ReactElement {
  const latest = grn.latest_appeal;
  const onTime = grn.on_time_status;
  const wf = grn.workflow_status;
  const pill = (label: string, cls: string) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>
  );
  if (!latest) {
    if (wf === 'acknowledged') return pill('Acknowledged', 'bg-green-100 text-green-800');
    if (onTime === 'on_time') return <span className="text-gray-400">-</span>;
    return pill('Not Appealed', 'bg-gray-100 text-gray-500');
  }
  switch (latest.status) {
    case 'awaiting_response':
      return pill(
        latest.is_customer_appeal ? 'Awaiting Supplier Response' : 'Awaiting Customer Response',
        'bg-yellow-100 text-yellow-800'
      );
    case 'accepted':
      return pill('Accepted', 'bg-green-100 text-green-800');
    case 'queried':
      return pill('Query – Awaiting Supplier Response', 'bg-blue-100 text-blue-800');
    case 'rejected':
      return pill('Rejected', 'bg-red-100 text-red-800');
    case 'cancelled':
      return pill('Appeal Cancelled', 'bg-gray-200 text-gray-600');
  }
}

interface RowActionsProps {
  grn: GoodsReceiptNote;
  companyType: string | undefined;
  busy: boolean;
  onAcknowledge: (grn: GoodsReceiptNote) => void;
  onAppeal: (grn: GoodsReceiptNote) => void;
  onCancelAppeal: (grn: GoodsReceiptNote) => void;
  onRespondAppeal: (grn: GoodsReceiptNote) => void;
}

/** CR-4648: Per-row action cluster. Mirrors the gating logic in
 * DeliveryDetailsModal's footer but scoped to a single GRN row so the
 * drill-down can act on each sibling delivery directly. */
function RowActions({
  grn, companyType, busy,
  onAcknowledge, onAppeal, onCancelAppeal, onRespondAppeal,
}: RowActionsProps) {
  const latest = grn.latest_appeal;
  const isOnTime = grn.on_time_status === 'on_time';
  const isEarlyOrLate = grn.on_time_status === 'early' || grn.on_time_status === 'late';
  const isPendingReview = grn.workflow_status === 'pending_review';
  const isAcknowledged = grn.workflow_status === 'acknowledged';

  const dash = <span className="text-gray-400">-</span>;
  if (isOnTime && !latest) return dash;
  if (isAcknowledged && (!latest || latest.status === 'cancelled')) return dash;

  const btnBase = 'px-2 py-1 rounded text-xs font-medium flex items-center gap-1 disabled:opacity-50';

  if (companyType === 'supplier') {
    const ownActiveAppeal = latest && !latest.is_customer_appeal
      && (latest.status === 'awaiting_response' || latest.status === 'queried');
    return (
      <div className="flex flex-wrap gap-2">
        {/* Pre-appeal: Acknowledge + Appeal side by side */}
        {isPendingReview && isEarlyOrLate && !ownActiveAppeal && (
          <>
            <button
              onClick={() => onAcknowledge(grn)} disabled={busy}
              className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
            >
              <CheckCircle className="h-3.5 w-3.5" /> Acknowledge
            </button>
            {grn.can_appeal && (
              <button
                onClick={() => onAppeal(grn)} disabled={busy}
                className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
              >
                <AlertTriangle className="h-3.5 w-3.5" /> Appeal
              </button>
            )}
          </>
        )}
        {/* Queried by customer → Update Appeal */}
        {latest?.status === 'queried' && !latest.is_customer_appeal && (
          <button
            onClick={() => onAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Update Appeal
          </button>
        )}
        {/* Active supplier-raised appeal → Cancel */}
        {ownActiveAppeal && (
          <button
            onClick={() => onCancelAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-gray-500 text-white hover:bg-gray-600`}
          >
            <X className="h-3.5 w-3.5" /> Cancel Appeal
          </button>
        )}
        {/* Acknowledged but no actionable supplier branch fires → dash */}
        {!isPendingReview && !ownActiveAppeal && latest?.status !== 'queried' && dash}
      </div>
    );
  }

  if (companyType === 'customer' || companyType === 'helix') {
    const hasPendingFromSupplier = latest?.status === 'awaiting_response' && !latest.is_customer_appeal;
    const hasResolvedAppeal = latest && (latest.status === 'rejected' || latest.status === 'accepted');
    return (
      <div className="flex flex-wrap gap-2">
        {hasPendingFromSupplier && (
          <button
            onClick={() => onRespondAppeal(grn)} disabled={busy}
            className={`${btnBase} bg-blue-600 text-white hover:bg-blue-700`}
          >
            <CheckCircle className="h-3.5 w-3.5" /> Respond to Appeal
          </button>
        )}
        {!hasPendingFromSupplier && !hasResolvedAppeal && isPendingReview && isEarlyOrLate && (
          <>
            <button
              onClick={() => onAcknowledge(grn)} disabled={busy}
              className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
            >
              <CheckCircle className="h-3.5 w-3.5" /> Acknowledge
            </button>
            {grn.can_appeal && (
              <button
                onClick={() => onAppeal(grn)} disabled={busy}
                className={`${btnBase} bg-orange-600 text-white hover:bg-orange-700`}
              >
                <AlertTriangle className="h-3.5 w-3.5" /> Appeal
              </button>
            )}
          </>
        )}
        {!hasPendingFromSupplier && hasResolvedAppeal && dash}
        {!hasPendingFromSupplier && !hasResolvedAppeal && !isPendingReview && dash}
      </div>
    );
  }

  return dash;
}

export default function GRNScoreChart({ poLineId, poNumber, lineNumber }: GRNScoreChartProps) {
  const [data, setData] = useState<{ name: string; value: number; color: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [grns, setGrns] = useState<GoodsReceiptNote[]>([]);
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [actionGrn, setActionGrn] = useState<GoodsReceiptNote | null>(null);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);
  const [appealOpen, setAppealOpen] = useState(false);
  const [respondAppeal, setRespondAppeal] = useState<DeliveryAppeal | null>(null);
  const [busy, setBusy] = useState(false);
  const { formatDate } = useFormatters();
  const { user } = useAuth();
  const companyType = user?.company_type;

  const fetchData = async () => {
    try {
      const res = await purchasingAPI.getGRNs({ po_line: poLineId, page_size: 100 });
      const grnList: GoodsReceiptNote[] = res.data.results || [];
      setGrns(grnList);
      const counts: Record<string, number> = { on_time: 0, early: 0, late: 0, unknown: 0 };
      for (const grn of grnList) {
        const status = grn.on_time_status || 'unknown';
        counts[status] = (counts[status] || 0) + 1;
      }
      setTotal(grnList.length);
      setData(
        Object.entries(counts)
          .filter(([, v]) => v > 0)
          .map(([key, value]) => ({
            name: LABELS[key] || key,
            value,
            color: COLORS[key] || COLORS['unknown'],
          }))
      );
    } catch {
      // ignore — empty list shows "no deliveries"
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poLineId]);

  if (total === 0) return null;

  const getStatusBadge = (status: string) => {
    const color = COLORS[status] || COLORS['unknown'];
    const label = LABELS[status] || 'Unknown';
    return (
      <span
        className="px-2 py-1 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: color }}
      >
        {label}
      </span>
    );
  };

  const handleAcknowledge = (grn: GoodsReceiptNote) => {
    setActionGrn(grn);
    setAcknowledgeOpen(true);
  };
  const handleAppeal = (grn: GoodsReceiptNote) => {
    setActionGrn(grn);
    setAppealOpen(true);
  };
  const handleRespondAppeal = async (grn: GoodsReceiptNote) => {
    if (!grn.latest_appeal?.id) return;
    try {
      setBusy(true);
      const res = await purchasingAPI.getDeliveryAppeal(grn.latest_appeal.id);
      setActionGrn(grn);
      setRespondAppeal(res.data as DeliveryAppeal);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load appeal'));
    } finally {
      setBusy(false);
    }
  };
  const handleCancelAppeal = async (grn: GoodsReceiptNote) => {
    if (!grn.latest_appeal?.id) return;
    if (!window.confirm('Are you sure you want to cancel this appeal?')) return;
    try {
      setBusy(true);
      await purchasingAPI.cancelDeliveryAppeal(grn.latest_appeal.id);
      toast.success('Appeal cancelled');
      await fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to cancel appeal'));
    } finally {
      setBusy(false);
    }
  };

  const handleAcknowledgeSubmit = async (data: GRNWorkflowData) => {
    try {
      await purchasingAPI.acknowledgeGRN(data.grnId, { comments: data.comments });
      toast.success('Delivery acknowledged');
      setAcknowledgeOpen(false);
      setActionGrn(null);
      await fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to acknowledge delivery'));
      throw err;
    }
  };

  const refreshAfterAppealAction = async () => {
    setAppealOpen(false);
    setActionGrn(null);
    await fetchData();
  };

  const handleRespondSuccess = async () => {
    setRespondAppeal(null);
    setActionGrn(null);
    await fetchData();
  };

  return (
    <>
      <div className="mt-4 pt-4 border-t">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Delivery Performance (PO Line)</h4>
        <div
          className="flex items-center gap-4 cursor-pointer hover:bg-gray-50 rounded p-2 -m-2 transition-colors"
          onClick={() => setShowDrilldown(true)}
          title="Click to view details"
        >
          <ResponsiveContainer width={120} height={120}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={50}
                dataKey="value"
                strokeWidth={1}
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [`${value} / ${total}`, '']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 text-xs">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
                <span>{entry.name}: {entry.value} ({Math.round((entry.value / total) * 100)}%)</span>
              </div>
            ))}
            <div className="text-gray-400 mt-1">Total: {total} deliveries (click for details)</div>
          </div>
        </div>
      </div>

      {/* CR-4614: Drill-down modal. CR-4648: + Appeal Status / Action columns. */}
      {showDrilldown && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[80]" onClick={() => setShowDrilldown(false)} />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="text-lg font-bold">
                  Delivery Performance
                  {poNumber && <span className="text-sm font-normal text-gray-500 ml-2">PO: {poNumber}{lineNumber ? ` / Line ${lineNumber}` : ''}</span>}
                </h3>
                <button onClick={() => setShowDrilldown(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-2 px-3 font-medium">Delivery Date</th>
                      <th className="text-left py-2 px-3 font-medium">GRN Number</th>
                      <th className="text-right py-2 px-3 font-medium">Qty Delivered</th>
                      <th className="text-left py-2 px-3 font-medium">On-Time Status</th>
                      <th className="text-left py-2 px-3 font-medium">Appeal Status</th>
                      <th className="text-left py-2 px-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grns.map((grn) => (
                      <tr key={grn.id} className="border-b hover:bg-gray-50 align-top">
                        <td className="py-2 px-3">{formatDate(grn.receipt_date)}</td>
                        <td className="py-2 px-3 font-mono">{grn.grn_number}</td>
                        <td className="py-2 px-3 text-right">{grn.quantity_received}</td>
                        <td className="py-2 px-3">{getStatusBadge(grn.on_time_status || 'unknown')}</td>
                        <td className="py-2 px-3">{appealStatusCell(grn)}</td>
                        <td className="py-2 px-3">
                          <RowActions
                            grn={grn}
                            companyType={companyType}
                            busy={busy}
                            onAcknowledge={handleAcknowledge}
                            onAppeal={handleAppeal}
                            onCancelAppeal={handleCancelAppeal}
                            onRespondAppeal={handleRespondAppeal}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-3">Showing {grns.length} of {total} deliveries</p>
              </div>
              <div className="flex justify-end p-5 border-t">
                <button
                  onClick={() => setShowDrilldown(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CR-4648: per-row action modals. Reuse the same components the
          parent Delivery Details footer launches, but scoped to the
          row's GRN so the user doesn't have to close + reopen the
          drill-down to act on a sibling delivery. */}
      {acknowledgeOpen && actionGrn && (
        <AcknowledgeGRNModal
          isOpen={acknowledgeOpen}
          onClose={() => { setAcknowledgeOpen(false); setActionGrn(null); }}
          grnId={actionGrn.id}
          grnNumber={actionGrn.grn_number}
          poNumber={actionGrn.po_number || ''}
          partNumber={actionGrn.part_number || ''}
          onSubmit={handleAcknowledgeSubmit}
        />
      )}
      {appealOpen && actionGrn && (
        <RaiseAppealModal
          isOpen={appealOpen}
          onClose={() => { setAppealOpen(false); setActionGrn(null); }}
          grn={actionGrn}
          existingAppealId={
            actionGrn.latest_appeal && (actionGrn.latest_appeal.status as DeliveryAppealStatus) === 'queried'
              ? actionGrn.latest_appeal.id
              : undefined
          }
          onSuccess={refreshAfterAppealAction}
        />
      )}
      {respondAppeal && (
        <AppealResponseModal
          isOpen={!!respondAppeal}
          onClose={() => { setRespondAppeal(null); setActionGrn(null); }}
          appeal={respondAppeal}
          onSuccess={handleRespondSuccess}
        />
      )}
    </>
  );
}
